import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, CalendarClock, Download, FileArchive, ImageIcon, KeyRound, ShieldCheck, Trash2, Undo2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";
import { useAdvertiser } from "./useAdvertiser";

const CONFIRM_WORD = "DELETE";

// axios returns a Blob for failed downloads too; read the JSON error out of it
async function blobErrMsg(e) {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed?.detail) return typeof parsed.detail === "string" ? parsed.detail : "Couldn't prepare your export. Please try again.";
    } catch (_) {}
  }
  return errMsg(e, "Couldn't prepare your export. Please try again.");
}

function DeleteDialog({ open, onOpenChange, onDeleted }) {
  const [word, setWord] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setWord("");
      setCode("");
      setError("");
      setBusy(false);
    }
  }, [open]);

  const ready = word.trim().toUpperCase() === CONFIRM_WORD && code.length === 6;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/advertiser/account/delete", { confirm: word.trim(), code });
      onDeleted?.(data.deletion);
      onOpenChange(false);
    } catch (err) {
      setError(errMsg(err));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="border-lux-border bg-lux-surface text-lux-text sm:max-w-md" data-testid="delete-account-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl font-medium">
            <Trash2 className="h-5 w-5 text-lux-error" aria-hidden="true" /> Delete your account?
          </DialogTitle>
          <DialogDescription className="text-lux-muted">
            Your profile goes offline straight away. You then have 30 days to change your mind before everything is permanently deleted.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label htmlFor="delete-confirm-word" className="lux-label">Type <span className="font-semibold text-lux-text">{CONFIRM_WORD}</span> to confirm</label>
            <input
              id="delete-confirm-word"
              className="lux-input mt-2 uppercase tracking-[0.2em]"
              value={word}
              onChange={(e) => setWord(e.target.value)}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder={CONFIRM_WORD}
              data-testid="delete-account-confirm-input"
            />
          </div>
          <div>
            <label htmlFor="delete-totp" className="lux-label flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" aria-hidden="true" /> Current 6-digit authenticator code</label>
            <div className="mt-2">
              <InputOTP id="delete-totp" maxLength={6} value={code} onChange={setCode} containerClassName="gap-2" data-testid="delete-account-totp-input">
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="h-12 w-12 border-lux-border bg-lux-surface2 text-lg" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <p className="mt-2 text-xs text-lux-muted">We ask for your authenticator code so nobody with access to your open session can delete your account.</p>
          </div>
          {error && <p className="text-sm text-lux-error" role="alert" data-testid="delete-account-error">{error}</p>}
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={() => onOpenChange(false)} disabled={busy} className="lux-btn lux-btn-ghost" data-testid="delete-account-cancel-button">
              Keep my account
            </button>
            <button type="submit" disabled={!ready || busy} className="lux-btn lux-btn-danger-solid" data-testid="delete-account-submit-button">
              <Trash2 className="h-4 w-4" aria-hidden="true" /> {busy ? "Scheduling..." : "Delete my account"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Fact({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-[10px] border border-lux-border bg-lux-surface2 p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-lux-silver">
        <Icon className="h-4 w-4" aria-hidden="true" />
        <p className="lux-label">{label}</p>
      </div>
      <p className="mt-2 truncate font-serif text-xl text-lux-text">{value}</p>
    </div>
  );
}

export default function AccountPage() {
  useSeo({ title: "Account & data | LUX", ...PRIVATE_SEO });
  const client = useQueryClient();
  const { me } = useAdvertiser();
  const { data: account, isLoading, refetch } = useQuery({
    queryKey: ["advertiser", "account"],
    queryFn: async () => (await api.get("/advertiser/account")).data,
  });
  const [exporting, setExporting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const refreshAll = async () => {
    await Promise.all([refetch(), client.invalidateQueries({ queryKey: ["advertiser", "me"] })]);
  };

  const download = async () => {
    setExporting(true);
    try {
      const res = await api.get("/advertiser/account/export", { responseType: "blob" });
      const name = res.headers?.["x-export-filename"] || `lux-data-export-${new Date().toISOString().slice(0, 10)}.zip`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Your data export is downloading", { description: name });
      refetch();
    } catch (e) {
      toast.error(await blobErrMsg(e));
    } finally {
      setExporting(false);
    }
  };

  const cancelDeletion = async () => {
    setCancelling(true);
    try {
      await api.post("/advertiser/account/delete/cancel");
      toast.success("Deletion cancelled", { description: "Your account is active again and your approved profile is visible." });
      await refreshAll();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setCancelling(false);
    }
  };

  const deletion = account?.deletion || me?.deletion || null;

  if (isLoading && !account) {
    return <div className="text-sm text-lux-muted" data-testid="account-loading">Loading your account...</div>;
  }

  return (
    <div className="space-y-8" data-testid="account-page">
      <header>
        <p className="lux-label">Account & data</p>
        <h1 className="mt-2 font-serif text-4xl text-lux-text sm:text-5xl">Your account</h1>
        <p className="mt-3 max-w-2xl text-sm text-lux-muted">
          Under UK GDPR you can take a copy of everything LUX holds about you and delete your account yourself, at any time, without asking support.
        </p>
      </header>

      {deletion && (
        <section className="lux-card border-lux-error/35 bg-lux-error/5 p-5 sm:p-6" data-testid="account-deletion-pending-card" aria-live="polite">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="lux-label flex items-center gap-1.5 text-lux-error"><CalendarClock className="h-3.5 w-3.5" aria-hidden="true" /> Deletion scheduled</p>
              <h2 className="mt-2 font-serif text-2xl text-lux-text">Your account will be permanently deleted on <span data-testid="account-deletion-due-date">{fmtDate(deletion.due_at)}</span></h2>
              <p className="mt-2 text-sm text-lux-text/85">
                Your profile is already offline. <strong className="text-lux-silverBright" data-testid="account-deletion-days-left">{deletion.days_left} day{deletion.days_left === 1 ? "" : "s"}</strong> left to change your mind.
                After that date your account, listing, photos, stories and verification records are erased and cannot be recovered.
              </p>
              <p className="mt-1 text-xs text-lux-muted">Requested {fmtDate(deletion.requested_at, true)}</p>
            </div>
            <div className="flex shrink-0 flex-col gap-2 sm:w-56">
              <button onClick={cancelDeletion} disabled={cancelling} className="lux-btn lux-btn-primary w-full" data-testid="account-cancel-deletion-button">
                <Undo2 className="h-4 w-4" aria-hidden="true" /> {cancelling ? "Restoring..." : "Cancel deletion"}
              </button>
              <button onClick={download} disabled={exporting} className="lux-btn lux-btn-secondary w-full" data-testid="account-export-before-deletion-button">
                <Download className="h-4 w-4" aria-hidden="true" /> {exporting ? "Preparing..." : "Download my data first"}
              </button>
            </div>
          </div>
        </section>
      )}

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" aria-label="Account summary">
        <Fact icon={ShieldCheck} label="Signed in as" value={account?.email_masked || me?.email_masked || "\u2014"} testId="account-fact-email" />
        <Fact icon={CalendarClock} label="Member since" value={account?.created_at ? fmtDate(account.created_at) : "\u2014"} testId="account-fact-since" />
        <Fact icon={ImageIcon} label="Photos stored" value={account?.photos ?? 0} testId="account-fact-photos" />
        <Fact icon={FileArchive} label="Stories stored" value={account?.stories ?? 0} testId="account-fact-stories" />
      </section>

      <section className="lux-card p-5 sm:p-6" data-testid="account-export-card">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="lux-label">Your data</p>
            <h2 className="mt-2 font-serif text-2xl text-lux-text">Download a copy of your data</h2>
            <p className="mt-2 max-w-xl text-sm text-lux-muted">A single ZIP file prepared on the spot. It contains:</p>
            <ul className="mt-3 space-y-1.5 text-sm text-lux-text/85">
              <li className="flex gap-2"><FileArchive className="mt-0.5 h-4 w-4 shrink-0 text-lux-silver" aria-hidden="true" /> <span><strong className="text-lux-text">lux-account.json</strong> - account details, verification results, your profile, approved and pending listing text, contact details, moderation decisions, tickets, notifications and daily statistics.</span></li>
              <li className="flex gap-2"><ImageIcon className="mt-0.5 h-4 w-4 shrink-0 text-lux-silver" aria-hidden="true" /> <span><strong className="text-lux-text">photos/</strong> - your original uploads, metadata removed and <em>without</em> the LUX watermark.</span></li>
            </ul>
            {account?.export_last_at && (
              <p className="mt-3 text-xs text-lux-muted" data-testid="account-export-last">Last export {fmtDate(account.export_last_at, true)}</p>
            )}
          </div>
          <div className="shrink-0 sm:w-56">
            <button onClick={download} disabled={exporting} className="lux-btn lux-btn-primary w-full" data-testid="account-export-button" aria-busy={exporting}>
              <Download className="h-4 w-4" aria-hidden="true" /> {exporting ? "Preparing ZIP..." : "Download ZIP"}
            </button>
            <p className="mt-2 text-center text-[11px] text-lux-muted">Keep the file private. It contains personal data.</p>
          </div>
        </div>
      </section>

      {!deletion && (
        <section className="lux-card border-lux-error/30 p-5 sm:p-6" data-testid="account-delete-card">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="lux-label flex items-center gap-1.5 text-lux-error"><AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" /> Delete account</p>
              <h2 className="mt-2 font-serif text-2xl text-lux-text">Leave LUX</h2>
              <ul className="mt-3 space-y-1.5 text-sm text-lux-text/85">
                <li className="flex gap-2"><span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-lux-error/80" aria-hidden="true" /> <span>Your profile and stories disappear from the site immediately.</span></li>
                <li className="flex gap-2"><span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-lux-error/80" aria-hidden="true" /> <span>You have <strong className="text-lux-text">30 days</strong> to sign back in and cancel. Nothing else changes in that time.</span></li>
                <li className="flex gap-2"><span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-lux-error/80" aria-hidden="true" /> <span>After 30 days your account, listing, photos, verification records, tickets and statistics are erased for good.</span></li>
                <li className="flex gap-2"><span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-lux-error/80" aria-hidden="true" /> <span>As set out in our Privacy notice, the staff audit trail and hashed safety records are kept. They contain no readable personal data.</span></li>
              </ul>
              <p className="mt-3 text-xs text-lux-muted">You will be asked to type {CONFIRM_WORD} and enter your current authenticator code.</p>
            </div>
            <div className="shrink-0 sm:w-56">
              <button onClick={() => setDialogOpen(true)} className="lux-btn lux-btn-danger w-full" data-testid="account-delete-button">
                <Trash2 className="h-4 w-4" aria-hidden="true" /> Delete my account
              </button>
            </div>
          </div>
        </section>
      )}

      <DeleteDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onDeleted={async (d) => {
          toast.success("Deletion scheduled", { description: `Your profile is offline. Everything is erased on ${fmtDate(d?.due_at)} unless you cancel.` });
          await refreshAll();
        }}
      />
    </div>
  );
}
